'use client';

import { useEffect, useState, useCallback } from 'react';
import { brandColors, neutralColors } from '@/platform/design-system/tokens/colors';

export interface ThemeSettings {
  brandPrimary?: string;
  brandPrimaryFg?: string;
  radius?: string;
  fontSans?: string;
  [key: string]: string | undefined;
}

const brandKeys = Object.keys(brandColors) as unknown as Array<keyof typeof brandColors>;
const neutralKeys = Object.keys(neutralColors) as unknown as Array<keyof typeof neutralColors>;

const themeField = (palette: 'brand' | 'neutral', shade: string) => `${palette}${shade}`;

const FONT_FALLBACKS = ", -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

// Dynamically injects a Google Fonts stylesheet so any family name saved in
// the design system is actually downloaded by the browser.
export function loadGoogleFont(family: string) {
  const name = family.trim();
  if (!name || !/^[A-Za-z0-9][A-Za-z0-9 ]{0,49}$/.test(name)) return;
  const href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(name).replace(/%20/g, '+')}:wght@400;500;600;700&display=swap`;
  const id = `google-font-${name.toLowerCase().replace(/\s+/g, '-')}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
}

// Report templates use display fonts that are not needed at app startup;
// pages that render reports call this so the fonts download only then.
const REPORT_FONTS = ['Urbanist', 'Fira Sans'];
let reportFontsRequested = false;
export function loadReportFonts() {
  if (reportFontsRequested) return;
  reportFontsRequested = true;
  for (const f of REPORT_FONTS) loadGoogleFont(f);
}

export function applyThemeToDocument(settings: Record<string, string | undefined>) {
  const root = document.documentElement;

  for (const shade of brandKeys) {
    const val = settings[themeField('brand', String(shade))];
    if (val) root.style.setProperty(`--brand-${shade}`, val);
  }

  for (const shade of neutralKeys) {
    const val = settings[themeField('neutral', String(shade))];
    if (val) root.style.setProperty(`--neutral-${shade}`, val);
  }

  if (settings.brandPrimary) root.style.setProperty('--primary', settings.brandPrimary);
  if (settings.brandPrimaryFg) root.style.setProperty('--primary-foreground', settings.brandPrimaryFg);
  if (settings.radius) root.style.setProperty('--radius', settings.radius);
  if (settings.fontSans) root.style.setProperty('--font-sans', settings.fontSans);
  if (settings.fontFamily) {
    root.style.setProperty(`--font-sans`, `'${settings.fontFamily}'${FONT_FALLBACKS}`);
    loadGoogleFont(settings.fontFamily);
  }
  if (settings.headingFontFamily) {
    root.style.setProperty(`--font-heading`, `'${settings.headingFontFamily}'${FONT_FALLBACKS}`);
    loadGoogleFont(settings.headingFontFamily);
  } else if (settings.fontFamily) {
    root.style.removeProperty('--font-heading');
  }

  for (const [settingKey, slug] of Object.entries(PILLAR_SETTING_SLUGS)) {
    const val = settings[settingKey];
    if (val) root.style.setProperty(`--pillar-${slug}`, val);
  }
}

const PILLAR_SETTING_SLUGS: Record<string, string> = {
  pillarHigherPurpose: 'higher-purpose',
  pillarPurposeStrategy: 'purpose-strategy',
  pillarConsciousCommunication: 'conscious-communication',
  pillarEngagedPeople: 'engaged-people',
  pillarConnectedCustomers: 'connected-customers',
  pillarAgility: 'agility',
};

let cachedSettings: Record<string, string | undefined> | null = null;
let cacheTimestamp = 0;
const CACHE_TTL = 60_000;
const STORAGE_KEY = 'platform-theme-settings';

function readStoredSettings(): Record<string, string | undefined> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function storeSettings(settings: Record<string, string | undefined>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Storage may be unavailable (private mode); ignore.
  }
}

export function useThemeSettings() {
  const [settings, setSettings] = useState<Record<string, string | undefined>>(
    () => cachedSettings ?? readStoredSettings() ?? {}
  );
  const [loading, setLoading] = useState(() => !cachedSettings && !readStoredSettings());

  useEffect(() => {
    // Paint the last known branding immediately, then revalidate quietly.
    const stored = cachedSettings ?? readStoredSettings();
    if (stored) {
      applyThemeToDocument(stored);
      setLoading(false);
    }

    if (cachedSettings && Date.now() - cacheTimestamp < CACHE_TTL) {
      setSettings(cachedSettings);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/api/theme-settings`, {
          headers: { apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const globalSettings = data?.global ?? {};
        const typographySettings = data?.typography ?? {};
        const merged = { ...globalSettings, ...typographySettings };
        cachedSettings = merged;
        cacheTimestamp = Date.now();
        storeSettings(merged);
        setSettings(merged);
        applyThemeToDocument(merged);
      } catch {
        // Use CSS defaults on error
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/api/theme-settings`, {
        headers: { apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string },
      });
      if (!res.ok) return;
      const data = await res.json();
      const globalSettings = data?.global ?? {};
      const typographySettings = data?.typography ?? {};
      const merged = { ...globalSettings, ...typographySettings };
      cachedSettings = merged;
      cacheTimestamp = Date.now();
      storeSettings(merged);
      setSettings(merged);
      applyThemeToDocument(merged);
    } catch {
      // Keep current state
    }
  }, []);

  return { settings, loading, refresh, applyThemeToDocument };
}

export function GlobalThemeLoader() {
  useThemeSettings();
  return null;
}
